import React, { useMemo } from 'react';
import '../../utils/quillSetup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

/**
 * Question Bank rich-text editor built on Quill.
 *
 * Supports:
 *   - Text formatting (bold, italic, underline, lists, headers)
 *   - Sub/superscript — essential for chemistry formulas (e.g. H₂SO₄)
 *   - Inline math/chemistry formulas via Quill's formula module (KaTeX-backed).
 *     Chemistry: write `\ce{H2SO4}` inside the formula popup — the mhchem
 *     extension (loaded in quillSetup.js) renders it correctly.
 *   - Inline images (inserted as base64 data URIs by default).
 *
 * The HTML string produced by Quill round-trips losslessly: when saved to the
 * database and loaded back, formulas and images re-render identically.
 */
function RichQuestionEditor({ value, onChange, placeholder, readOnly }) {
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ script: 'sub' }, { script: 'super' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['formula', 'image', 'link'],
        ['clean'],
      ],
      clipboard: { matchVisual: false },
    }),
    []
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'script',
    'list',
    'bullet',
    'align',
    'formula',
    'image',
    'link',
  ];

  return (
    <div className="qb-rich-editor">
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder || 'Type the question here… use ƒ for formulas, 🖼 for images.'}
        readOnly={!!readOnly}
      />
      <div className="qb-rich-editor-hint">
        Tip: click the <strong>ƒ</strong> button for math / chemistry formulas
        (LaTeX — e.g. <code>E=mc^2</code>, <code>\ce{'{'}H2O{'}'}</code>).
        Use the <strong>🖼</strong> button to insert images (diagrams, figures).
      </div>
    </div>
  );
}

export default RichQuestionEditor;
